import { brandTitleByKey } from '@prismamedia/one-brandkey';
import { map, property } from 'lodash';
import { FC, memo, useMemo } from 'react';
import { Link as RouterLink, generatePath } from 'react-router-dom';
import {
  ArticleFormat,
  ArticleHistory,
  ArticleStatus,
  GetArticlesDetailedQuery_query_articles_Article,
  GetArticlesQuery_query_articles_Article,
  GetArticlesQuery_query_articles_Article_categories_ArticleCategory,
  GetArticlesQuery_query_articles_Article_channels_ArticleChannel,
  SearchArticlesQuery_query_searchArticles_Article,
} from '../../../../../__generated__/queries-web';
import { useUpdateArticleLockerId } from '../../../../../apollo/mutations/article.web.graphql';
import { SeeArticleButton } from '../../../../../components/SeeArticleButton';
import { CardItem } from '../../../../../components/CardItem';
import { ListMediasIcons } from '../../../../../components/ListMediasIcons';
import { paths } from '../../../../../routing/Router/paths';
import { auth } from '../../../../../utils/auth';
import { formatDate } from '../../../../../utils/dateUtils';
import { getImageFromArticleBody } from '../../../../../utils/getImageFromArticleBody';
import {
  getImageFromMedias,
  getVideoThumbnail,
} from '../../../../../utils/media';
import { statusToLabel } from '../../../../../utils/statuses';
import { standardizeArticleUrl } from '../../../../../utils/url';
import { useMediasIcons } from '../../../../../utils/useMediasIcons';
import { PAID_ARTICLE } from './constants';
import { ArticleItemStyleProps, useStyles } from './styles';

type ArticleItemProps = (
  | GetArticlesDetailedQuery_query_articles_Article
  | GetArticlesQuery_query_articles_Article
  | SearchArticlesQuery_query_searchArticles_Article
) & {
  brand?: string | null;
};

export const ArticleItem: FC<ArticleItemProps> = memo(
  ({
    id,
    title,
    status,
    publishedAt,
    updatedAt,
    events = [],
    categories = [],
    channels = [],
    creator,
    histories = [],
    brandKey,
    brand,
    format,
    medias,
    pushCount,
    live,
    canonical,
    lockedBy,
    qualifiers,
    newsletterFlashSenderHistories,
    ...rest
  }) => {
    const body =
      'body' in rest && rest.body !== undefined ? rest.body : undefined;
    const updateLockerId = useUpdateArticleLockerId();
    const classes = useStyles() as ArticleItemStyleProps;
    const currentBrand = brand || '';
    const articleLink =
      format === ArticleFormat.Slideshow
        ? `${generatePath(paths.SLIDESHOW_EDIT, { id })}${
            currentBrand !== '' ? `?brand=${currentBrand}` : ''
          }`
        : generatePath(paths.ARTICLE_EDIT, {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            brandKey: brandKey!,
            articleType: format === ArticleFormat.Rich ? 'article' : 'video',
            search: brand ? 'search' : undefined,
            id,
          });
    const isLocked = lockedBy && lockedBy.id === auth.user?.id;
    const articleUsers = histories?.filter(
      (hist) => hist?.user?.__typename === 'OneUser' && 'name' in hist.user,
    ) as ArticleHistory[];

    const articleCreator = creator?.name || 'Inconnu';

    const articleModificator =
      articleUsers && articleUsers.length > 0
        ? articleUsers[articleUsers.length - 1].user?.name
        : 'Inconnu';

    const lastHistoryDate = histories?.[histories.length - 1]?.createdAt;
    const lastModifiedDate =
      lastHistoryDate && updatedAt && lastHistoryDate >= updatedAt
        ? lastHistoryDate
        : updatedAt;

    const image = useMemo(() => {
      switch (format) {
        case ArticleFormat.Slideshow:
          return getImageFromArticleBody(body, true);
        case ArticleFormat.Video:
          return getVideoThumbnail(medias);
        default:
          return getImageFromMedias(medias).mediaForDisplay;
      }
    }, [format, body, medias]);

    const displayTitle = (
      articleData:
        | GetArticlesQuery_query_articles_Article_channels_ArticleChannel[]
        | GetArticlesQuery_query_articles_Article_categories_ArticleCategory[],
    ) => map(articleData, property('category.title'));

    const categoryTitle: any = useMemo(() => {
      const _title =
        format === ArticleFormat.Video
          ? displayTitle(channels)
          : displayTitle(categories);
      if (!_title.length) {
        return null;
      }
      return _title.length === 2 ? `${_title[0]} / ${_title[1]}` : _title[0];
    }, [format, categories, channels]);

    const eventTitle = useMemo(() => {
      if (events.length > 0) {
        return events.length === 2
          ? `${events[0].category.title}, ${events[1].category.title}`
          : events[0].category.title;
      }
    }, [events]);

    const publicUrl = standardizeArticleUrl(canonical) || '';
    const displaySeeArticle = !!publicUrl;
    const isPublished = status === ArticleStatus.Published;
    const isScheduled = status === ArticleStatus.Scheduled;

    const mediasIcons = useMediasIcons(medias, live?.postCount || 0, pushCount);
    const isPaidArticle = qualifiers?.some(
      ({ qualifier }) => qualifier.title === PAID_ARTICLE,
    );

    return (
      <RouterLink
        to={articleLink}
        className={classes.link}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <CardItem
          titleOverflow={true}
          newsletterFlashSenderHistories={newsletterFlashSenderHistories}
          hasPush={Boolean(pushCount)}
          pushCount={pushCount}
          isPaidArticle={isPaidArticle}
          ActionsMenu={
            (isPublished || isScheduled) &&
            displaySeeArticle && (
              <SeeArticleButton articleId={id} publicUrl={publicUrl} />
            )
          }
          Icons={<ListMediasIcons listMedias={mediasIcons} />}
          id={id}
          title={title}
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          media={image!}
          format={format}
          topTitle={`${articleCreator} - ${statusToLabel(format)[status]} ${
            publishedAt
              ? `le ${formatDate(new Date(publishedAt), 'dd/MM/yyyy à HH:mm')}`
              : ''
          } ${
            lastModifiedDate
              ? `(modifié le ${formatDate(
                  new Date(lastModifiedDate),
                  'dd/MM/yyy à HH:mm',
                )} par ${articleModificator})`
              : ''
          }`}
          brandKey={brandTitleByKey[brandKey]}
          eventTitle={eventTitle}
          categoryTitle={categoryTitle}
          lockedBy={lockedBy}
          isLocked={Boolean(isLocked)}
          updateLocker={updateLockerId}
        />
      </RouterLink>
    );
  },
);
